import Ionicons from "@react-native-vector-icons/ionicons";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { BrandColors, Colors } from "../../constants/theme";
import { useDesktopLayout } from "../../hooks/use-desktop-layout";
import {
  CONDITION_OPTIONS,
  FUEL_LEVELS,
  INSPECTION_CATEGORIES,
  INSPECTION_CHECKLIST_ITEMS,
} from "../../data/inspection-items";
import type { InspectionTierRequirements } from "../../lib/inspection-tier-requirements";
import type { InspectionItem } from "../../lib/services/inspections";
import { ThemedText } from "../themed-text";

interface InspectionChecklistProps {
  items: InspectionItem[];
  onItemsChange: (items: InspectionItem[]) => void;
  fuelLevel: string;
  onFuelLevelChange: (level: string) => void;
  odometerReading?: number;
  onOdometerReadingChange: (reading: number | undefined) => void;
  generalRemarks?: string;
  onGeneralRemarksChange?: (remarks: string) => void;
  requirements?: InspectionTierRequirements;
  vin?: string;
  onVinChange?: (value: string) => void;
  warningLightsPresent?: boolean | null;
  onWarningLightsPresentChange?: (value: boolean) => void;
  obdFaultCodes?: string;
  onObdFaultCodesChange?: (value: string) => void;
  vehicleSummary?: React.ReactNode;
}

const isWeb = Platform.OS === "web";
export function InspectionChecklist({
  items,
  onItemsChange,
  fuelLevel,
  onFuelLevelChange,
  odometerReading,
  onOdometerReadingChange,
  generalRemarks,
  onGeneralRemarksChange,
  requirements,
  vin,
  onVinChange,
  warningLightsPresent,
  onWarningLightsPresentChange,
  obdFaultCodes,
  onObdFaultCodesChange,
  vehicleSummary,
}: InspectionChecklistProps) {
  const desktop = useDesktopLayout();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const isDark = colorScheme === "dark";

  const scrollRef = useRef<ScrollView>(null);
  const tabScrollRef = useRef<ScrollView>(null);
  const stickyTabScrollRef = useRef<ScrollView>(null);
  const categoryOffsetsRef = useRef<Record<string, number>>({});
  const categorySectionYRef = useRef<Record<string, number>>({});
  const itemOffsetsRef = useRef<Record<string, number>>({});
  const tabOffsetsRef = useRef<Record<string, number>>({});
  const tabWidthsRef = useRef<Record<string, number>>({});
  const tabBarWidthRef = useRef(0);
  const checklistContainerYRef = useRef(0);
  const generalRemarksOffsetRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);

  const [activeCategory, setActiveCategory] = useState<string>("Exterior");
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [generalRemarksFocused, setGeneralRemarksFocused] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [remarksDrafts, setRemarksDrafts] = useState<Record<string, string>>(
    {},
  );
  const [tabBarOffsetY, setTabBarOffsetY] = useState<number | null>(null);
  const [showStickyTabs, setShowStickyTabs] = useState(false);

  const scrollToOffset = useCallback((offset: number) => {
    isProgrammaticScrollRef.current = true;
    if (isWeb) {
      return;
    }
    scrollRef.current?.scrollTo({
      y: Math.max(0, offset - 200),
      animated: true,
    });

    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 400);
  }, []);

  const scrollToCategory = useCallback(
    (categoryKey: string) => {
      const offset = categoryOffsetsRef.current[categoryKey];

      if (offset !== undefined) {
        scrollToOffset(offset);
      }
    },
    [scrollToOffset],
  );

  const scrollToItem = useCallback(
    (itemName: string) => {
      const offset = itemOffsetsRef.current[itemName];

      if (offset !== undefined) {
        scrollToOffset(offset);
      }
    },
    [scrollToOffset],
  );

  const cardBackground = isDark ? "#1a2332" : BrandColors.white;
  const inputBackground = isDark ? theme.background : BrandColors.white;

  const visibleCategories = useMemo(
    () =>
      requirements
        ? INSPECTION_CATEGORIES.filter((category) =>
            requirements.requiredCategories.includes(category.key),
          )
        : INSPECTION_CATEGORIES,
    [requirements],
  );

  const scrollTabBarToActive = useCallback(
    (categoryKey: string) => {
      const tabX = tabOffsetsRef.current[categoryKey];
      const tabWidth = tabWidthsRef.current[categoryKey];
      const barWidth = tabBarWidthRef.current;

      if (tabX === undefined || tabWidth === undefined || barWidth === 0) {
        return;
      }

      const centeredOffset = tabX - (barWidth - tabWidth) / 2;
      const lastCategory = visibleCategories[visibleCategories.length - 1];
      const lastTabX = tabOffsetsRef.current[lastCategory.key] ?? 0;
      const lastTabWidth = tabWidthsRef.current[lastCategory.key] ?? 0;
      const maxScrollX = Math.max(0, lastTabX + lastTabWidth - barWidth);
      const scrollX = Math.min(Math.max(0, centeredOffset), maxScrollX);

      tabScrollRef.current?.scrollTo({ x: scrollX, animated: true });
      stickyTabScrollRef.current?.scrollTo({ x: scrollX, animated: true });
    },
    [visibleCategories],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollTabBarToActive(activeCategory);
    }, 50);

    return () => clearTimeout(timer);
  }, [activeCategory, scrollTabBarToActive, showStickyTabs, visibleCategories]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollY = event.nativeEvent.contentOffset.y;

    if (tabBarOffsetY !== null) {
      setShowStickyTabs(scrollY >= tabBarOffsetY);
    }

    if (isProgrammaticScrollRef.current) {
      return;
    }

    const viewportOffset = scrollY + 80;
    let nextActiveCategory: string =
      visibleCategories[0]?.key ?? INSPECTION_CATEGORIES[0].key;

    for (const category of visibleCategories) {
      const offset = categoryOffsetsRef.current[category.key];

      if (offset !== undefined && viewportOffset >= offset) {
        nextActiveCategory = category.key;
      }
    }

    setActiveCategory((current) =>
      current === nextActiveCategory ? current : nextActiveCategory,
    );
  };

  useEffect(() => {
    const showEvent =
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent =
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";

    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      setKeyboardHeight(event.endCoordinates.height);
    });
    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  useEffect(() => {
    if (!generalRemarksFocused) {
      return;
    }

    const timer = setTimeout(
      () => {
        isProgrammaticScrollRef.current = true;
        scrollRef.current?.scrollTo({
          y: Math.max(0, generalRemarksOffsetRef.current - 48),
          animated: true,
        });

        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 400);
      },
      Platform.OS === "ios" ? 250 : 100,
    );

    return () => clearTimeout(timer);
  }, [generalRemarksFocused, keyboardHeight]);

  const groupedItems = useMemo(
    () =>
      INSPECTION_CHECKLIST_ITEMS.reduce(
        (acc, item) => {
          if (
            requirements &&
            !requirements.requiredCategories.includes(item.category)
          ) {
            return acc;
          }

          if (!acc[item.category]) {
            acc[item.category] = [];
          }

          acc[item.category].push(item);

          return acc;
        },
        {} as Record<string, (typeof INSPECTION_CHECKLIST_ITEMS)[number][]>,
      ),
    [requirements],
  );

  const flatItems = useMemo(
    () =>
      visibleCategories.flatMap((category) =>
        (groupedItems[category.key] ?? []).map((item) => ({
          ...item,
          category: category.key,
        })),
      ),
    [groupedItems, visibleCategories],
  );

  const totalItems = flatItems.length;
  const completedCount = items.filter((item) => item.condition).length;

  const handleConditionChange = (itemName: string, condition: string) => {
    const existingIndex = items.findIndex((i) => i.item_name === itemName);
    const draftRemarks = remarksDrafts[itemName] ?? "";

    if (existingIndex >= 0) {
      const updated = [...items];
      updated[existingIndex] = {
        ...updated[existingIndex],
        condition: condition as InspectionItem["condition"],
        remarks: updated[existingIndex].remarks || draftRemarks,
      };
      onItemsChange(updated);
    } else {
      onItemsChange([
        ...items,
        {
          item_name: itemName,
          condition: condition as InspectionItem["condition"],
          remarks: draftRemarks,
        },
      ]);
    }

    if (draftRemarks) {
      setRemarksDrafts((prev) => {
        const next = { ...prev };
        delete next[itemName];

        return next;
      });
    }

    const currentIndex = flatItems.findIndex((item) => item.name === itemName);

    if (currentIndex >= 0 && currentIndex < flatItems.length - 1) {
      const nextItem = flatItems[currentIndex + 1];

      setActiveCategory(nextItem.category);

      setTimeout(() => {
        scrollToItem(nextItem.name);
      }, 150);
    }
  };

  const handleRemarksChange = (itemName: string, remarks: string) => {
    const existingIndex = items.findIndex((i) => i.item_name === itemName);

    if (existingIndex >= 0) {
      const updated = [...items];
      updated[existingIndex] = {
        ...updated[existingIndex],
        remarks,
      };
      onItemsChange(updated);

      return;
    }

    setRemarksDrafts((prev) => ({ ...prev, [itemName]: remarks }));
  };

  const getItemCondition = (itemName: string) => {
    return items.find((i) => i.item_name === itemName)?.condition;
  };

  const getItemRemarks = (itemName: string) => {
    return (
      items.find((i) => i.item_name === itemName)?.remarks ??
      remarksDrafts[itemName] ??
      ""
    );
  };

  const renderChecklistItem = (
    item: (typeof INSPECTION_CHECKLIST_ITEMS)[number],
    itemNumber: number,
  ) => {
    const condition = getItemCondition(item.name);
    const remarks = getItemRemarks(item.name);
    const isExpanded = expandedItem === item.name;
    const selectedOption = CONDITION_OPTIONS.find(
      (option) => option.value === condition,
    );

    return (
      <View
        key={item.name}
        collapsable={false}
        onLayout={(event) => {
          const categorySectionY = categorySectionYRef.current[item.category];
          itemOffsetsRef.current[item.name] =
            checklistContainerYRef.current +
            (categorySectionY ?? 0) +
            event.nativeEvent.layout.y;
        }}
        style={[
          styles.checklistItem,
          {
            backgroundColor: cardBackground,
            borderColor: theme.border,
          },
        ]}
      >
        <View style={styles.itemHeader}>
          <View style={styles.itemTitleRow}>
            <View
              style={[
                styles.itemNumber,
                {
                  backgroundColor: isDark
                    ? "#2a3444"
                    : BrandColors.backgroundGray,
                },
              ]}
            >
              <ThemedText
                style={[styles.itemNumberText, { color: theme.icon }]}
                type="extraSmallSemiBold"
              >
                {itemNumber}
              </ThemedText>
            </View>
            <ThemedText style={styles.itemName} type="smallSemiBold">
              {item.name}
            </ThemedText>
          </View>
          {selectedOption && (
            <View
              style={[
                styles.conditionBadge,
                {
                  backgroundColor: isDark
                    ? `${selectedOption.color}33`
                    : selectedOption.badgeBg,
                },
              ]}
            >
              <ThemedText
                style={[
                  styles.conditionBadgeText,
                  { color: selectedOption.badgeText },
                ]}
                type="extraSmallSemiBold"
              >
                {selectedOption.label}
              </ThemedText>
            </View>
          )}
        </View>

        <View style={styles.conditionButtons}>
          {CONDITION_OPTIONS.map((option) => {
            const isSelected = condition === option.value;

            return (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.conditionButton,
                  {
                    borderColor: option.color,
                    backgroundColor: isSelected
                      ? option.color
                      : inputBackground,
                  },
                ]}
                onPress={() => {
                  Keyboard.dismiss();
                  handleConditionChange(item.name, option.value);
                }}
                activeOpacity={0.7}
              >
                <Ionicons
                  name={option.icon}
                  size={18}
                  color={isSelected ? BrandColors.white : option.color}
                />
              </TouchableOpacity>
            );
          })}
        </View>

        <TouchableOpacity
          style={styles.remarksToggle}
          onPress={() => setExpandedItem(isExpanded ? null : item.name)}
          activeOpacity={0.7}
        >
          <Ionicons
            name="chatbubble-outline"
            size={16}
            color={BrandColors.primary}
          />
          <ThemedText style={styles.remarksToggleText} type="extraSmall">
            Add remarks
          </ThemedText>
          <Ionicons
            name={isExpanded ? "chevron-up" : "chevron-down"}
            size={14}
            color={BrandColors.primary}
          />
        </TouchableOpacity>

        {isExpanded && (
          <TextInput
            style={[
              styles.remarksInput,
              {
                backgroundColor: cardBackground,
                borderColor: theme.border,
                color: theme.text,
              },
            ]}
            placeholder="Add any specific remarks or damage details..."
            placeholderTextColor={theme.icon}
            multiline
            numberOfLines={3}
            value={remarks}
            onChangeText={(text) => handleRemarksChange(item.name, text)}
          />
        )}
      </View>
    );
  };

  const renderSectionCard = (
    icon: React.ReactNode,
    title: string,
    required = false,
    children: React.ReactNode,
  ) => (
    <View
      style={[
        styles.card,
        {
          backgroundColor: cardBackground,
          borderColor: isDark ? theme.border : BrandColors.lightGray,
        },
      ]}
    >
      <View style={styles.cardHeader}>
        {icon}
        <ThemedText style={styles.cardTitle} type="smallSemiBold">
          {title}
          {required && <ThemedText style={styles.requiredMark}> *</ThemedText>}
        </ThemedText>
      </View>
      {children}
    </View>
  );

  const odometerBorderColor = isDark ? theme.border : BrandColors.lightGray;

  const renderCategoryTabs = (
    horizontalScrollRef: React.RefObject<ScrollView | null>,
  ) => (
    <ScrollView
      ref={horizontalScrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabBar}
      nestedScrollEnabled
      onLayout={(event) => {
        tabBarWidthRef.current = event.nativeEvent.layout.width;
      }}
    >
      {visibleCategories.map((category) => {
        const isActive = activeCategory === category.key;

        return (
          <TouchableOpacity
            key={category.key}
            style={styles.tab}
            onLayout={(event) => {
              tabOffsetsRef.current[category.key] = event.nativeEvent.layout.x;
              tabWidthsRef.current[category.key] =
                event.nativeEvent.layout.width;
            }}
            onPress={() => {
              setActiveCategory(category.key);
              scrollToCategory(category.key);
            }}
            activeOpacity={0.7}
          >
            <Ionicons
              name={category.icon as any}
              size={20}
              color={isActive ? BrandColors.primary : theme.icon}
            />
            <ThemedText
              style={[
                styles.tabLabel,
                { color: isActive ? BrandColors.primary : theme.icon },
              ]}
              type="extraSmall"
            >
              {category.label}
            </ThemedText>
            {isActive && <View style={styles.tabIndicator} />}
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  const inputCards = (
    <>
      {renderSectionCard(
        <Ionicons
          name="barcode-outline"
          size={20}
          color={BrandColors.primary}
        />,
        "VIN",
        requirements?.requireVin ?? false,
        <TextInput
          style={[
            styles.odometerInput,
            {
              backgroundColor: isDark ? theme.background : BrandColors.white,
              borderColor: odometerBorderColor,
              borderWidth: 1,
              borderRadius: 12,
              paddingHorizontal: 16,
              paddingVertical: 14,
              color: theme.text,
            },
          ]}
          placeholder="Enter vehicle VIN"
          placeholderTextColor={BrandColors.mediumGray}
          autoCapitalize="characters"
          value={vin ?? ""}
          onChangeText={onVinChange}
        />,
      )}

      {renderSectionCard(
        <Ionicons
          name="speedometer-outline"
          size={20}
          color={BrandColors.primary}
        />,
        "Odometer Reading",
        requirements?.requireOdometer ?? false,
        <View
          style={[
            styles.odometerInputRow,
            {
              backgroundColor: isDark ? theme.background : BrandColors.white,
              borderColor: odometerBorderColor,
            },
          ]}
        >
          <TextInput
            style={[styles.odometerInput, { color: theme.text }]}
            placeholder={
              requirements?.requireOdometer
                ? "Enter mileage"
                : "Enter mileage (optional)"
            }
            placeholderTextColor={BrandColors.mediumGray}
            keyboardType="numeric"
            value={odometerReading?.toString() || ""}
            onChangeText={(text) =>
              onOdometerReadingChange(
                text ? parseInt(text.replace(/[^0-9]/g, ""), 10) : undefined,
              )
            }
          />
          <View
            style={[
              styles.odometerSuffix,
              {
                borderLeftColor: odometerBorderColor,
                backgroundColor: isDark
                  ? "#2a3444"
                  : BrandColors.backgroundGray,
              },
            ]}
          >
            <ThemedText
              style={[styles.odometerSuffixText, { color: theme.icon }]}
              type="small"
            >
              mi
            </ThemedText>
          </View>
        </View>,
      )}

      {renderSectionCard(
        <Ionicons
          name="warning-outline"
          size={20}
          color={BrandColors.primary}
        />,
        "Warning Lights Present",
        requirements?.requireWarningLights ?? false,
        <View style={styles.warningLightsRow}>
          {[true, false].map((value) => {
            const isSelected = warningLightsPresent === value;

            return (
              <TouchableOpacity
                key={value ? "yes" : "no"}
                style={[
                  styles.warningLightsOption,
                  {
                    borderColor: isSelected
                      ? BrandColors.primary
                      : odometerBorderColor,
                    backgroundColor: isSelected
                      ? `${BrandColors.primary}10`
                      : inputBackground,
                  },
                ]}
                onPress={() => {
                  Keyboard.dismiss();
                  onWarningLightsPresentChange?.(value);
                }}
                activeOpacity={0.7}
              >
                <ThemedText type="smallSemiBold">
                  {value ? "Yes" : "No"}
                </ThemedText>
              </TouchableOpacity>
            );
          })}
        </View>,
      )}
    </>
  );

  const fuelLevelCard = renderSectionCard(
    <MaterialCommunityIcons
      name="gas-station-outline"
      size={20}
      color={BrandColors.primary}
    />,
    "Fuel Level",
    requirements?.requireFuelLevel ?? true,
    <View style={styles.fuelLevelRow}>
      {FUEL_LEVELS.map((level) => {
        const isSelected = fuelLevel === level.value;

        return (
          <TouchableOpacity
            key={level.value}
            style={[
              styles.fuelLevelOption,
              {
                backgroundColor: isDark ? theme.background : BrandColors.white,
                borderColor: isSelected
                  ? BrandColors.primary
                  : odometerBorderColor,
                borderWidth: isSelected ? 2 : 1,
              },
            ]}
            onPress={() => {
              Keyboard.dismiss();
              onFuelLevelChange(level.value);
            }}
            activeOpacity={0.7}
          >
            <View
              style={[styles.fuelLevelCircle, { backgroundColor: level.color }]}
            >
              <ThemedText
                style={styles.fuelLevelCircleText}
                type="extraSmallSemiBold"
              >
                {level.shortLabel}
              </ThemedText>
            </View>
            <ThemedText
              style={[
                styles.fuelLevelLabel,
                { color: isDark ? theme.text : BrandColors.charcoal },
              ]}
              type="extraSmall"
            >
              {level.label}
            </ThemedText>
          </TouchableOpacity>
        );
      })}
    </View>,
  );

  const checklistHeader = (
    <View
      style={[
        {
          backgroundColor: theme.background,
          borderColor: theme.border,
        },
      ]}
    >
      <View style={styles.checklistHeader}>
        <ThemedText style={styles.checklistTitle} type="smallSemiBold">
          Vehicle Condition Checklist
          <ThemedText style={styles.requiredMark}> *</ThemedText>
        </ThemedText>
        <ThemedText style={styles.checklistProgress} type="extraSmallSemiBold">
          {completedCount} / {totalItems} completed
        </ThemedText>
      </View>
    </View>
  );

  const remarksSections = (
    <>
      {requirements?.requireObd ? (
        <View
          style={[styles.section, { borderTopColor: theme.border }]}
          collapsable={false}
        >
          <ThemedText style={styles.sectionTitle} type="smallSemiBold">
            OBD Fault Codes
            <ThemedText style={styles.requiredMark}> *</ThemedText>
          </ThemedText>
          <ThemedText
            style={[styles.obdHint, { color: theme.icon }]}
            type="extraSmall"
          >
            Enter one fault code per line. Enter NONE if no codes were found.
          </ThemedText>
          <TextInput
            style={[
              styles.remarksInput,
              styles.generalRemarksInput,
              {
                backgroundColor: inputBackground,
                borderColor: theme.border,
                color: theme.text,
              },
            ]}
            placeholder="P0300&#10;P0420"
            placeholderTextColor={theme.icon}
            multiline
            numberOfLines={4}
            textAlignVertical="top"
            value={obdFaultCodes ?? ""}
            onChangeText={onObdFaultCodesChange}
          />
        </View>
      ) : null}

      <View
        style={[styles.section, { borderTopColor: theme.border }]}
        collapsable={false}
        onLayout={(event) => {
          generalRemarksOffsetRef.current =
            checklistContainerYRef.current + event.nativeEvent.layout.y;
        }}
      >
        <ThemedText style={styles.sectionTitle} type="smallSemiBold">
          General Remarks
        </ThemedText>
        <TextInput
          style={[
            styles.remarksInput,
            styles.generalRemarksInput,
            {
              backgroundColor: inputBackground,
              borderColor: theme.border,
              color: theme.text,
            },
          ]}
          placeholder="Add any general observations or notes about the vehicle..."
          placeholderTextColor={theme.icon}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          value={generalRemarks ?? ""}
          onChangeText={onGeneralRemarksChange}
          onFocus={() => setGeneralRemarksFocused(true)}
          onBlur={() => setGeneralRemarksFocused(false)}
        />
      </View>
    </>
  );

  if (desktop) {
    const activeItems = flatItems.filter(
      (item) => item.category === activeCategory,
    );
    const progressPercent =
      totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0;

    return (
      <View style={styles.desktopContainer}>
        <ScrollView
          style={styles.desktopLeftColumn}
          contentContainerStyle={styles.desktopLeftContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {vehicleSummary}
          {inputCards}
          {fuelLevelCard}
          <View>
            {checklistHeader}
            <View
              style={[
                styles.desktopProgressTrack,
                { backgroundColor: theme.border },
              ]}
            >
              <View
                style={[
                  styles.desktopProgressFill,
                  { width: `${progressPercent}%` },
                ]}
              />
            </View>
          </View>
          {remarksSections}
        </ScrollView>

        <View style={styles.desktopRightColumn}>
          <View
            style={[styles.desktopTabBar, { borderBottomColor: theme.border }]}
          >
            {visibleCategories.map((category) => {
              const isActive = activeCategory === category.key;

              return (
                <TouchableOpacity
                  key={category.key}
                  style={styles.desktopTab}
                  onPress={() => setActiveCategory(category.key)}
                  activeOpacity={0.7}
                >
                  <ThemedText
                    style={[
                      styles.desktopTabLabel,
                      { color: isActive ? BrandColors.primary : theme.icon },
                    ]}
                    type="extraSmall"
                  >
                    {category.label}
                  </ThemedText>
                  {isActive && <View style={styles.tabIndicator} />}
                </TouchableOpacity>
              );
            })}
          </View>

          <ScrollView
            contentContainerStyle={styles.desktopItemsContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View
              style={[
                styles.desktopItemsCard,
                { backgroundColor: cardBackground, borderColor: theme.border },
              ]}
            >
              {activeItems.map((item, index) => {
                const condition = getItemCondition(item.name);
                const isExpanded = expandedItem === item.name;
                const itemNumber =
                  flatItems.findIndex((flat) => flat.name === item.name) + 1;

                return (
                  <View
                    key={item.name}
                    style={[
                      styles.desktopItem,
                      index < activeItems.length - 1 && {
                        borderBottomWidth: 1,
                        borderBottomColor: theme.border,
                      },
                    ]}
                  >
                    <View style={styles.desktopItemRow}>
                      <View style={styles.itemTitleRow}>
                        <View
                          style={[
                            styles.itemNumber,
                            {
                              backgroundColor: isDark
                                ? "#2a3444"
                                : BrandColors.backgroundGray,
                            },
                          ]}
                        >
                          <ThemedText
                            style={[
                              styles.itemNumberText,
                              { color: theme.icon },
                            ]}
                            type="extraSmallSemiBold"
                          >
                            {itemNumber}
                          </ThemedText>
                        </View>
                        <ThemedText style={styles.itemName} type="small">
                          {item.name}
                        </ThemedText>
                      </View>

                      <View style={styles.desktopItemActions}>
                        <View style={styles.desktopConditionButtons}>
                          {CONDITION_OPTIONS.map((option) => {
                            const isSelected = condition === option.value;
                            const contentColor = isSelected
                              ? BrandColors.white
                              : option.color;

                            return (
                              <TouchableOpacity
                                key={option.value}
                                style={[
                                  styles.desktopConditionButton,
                                  {
                                    borderColor: option.color,
                                    backgroundColor: isSelected
                                      ? option.color
                                      : `${option.color}1A`,
                                  },
                                ]}
                                onPress={() =>
                                  handleConditionChange(item.name, option.value)
                                }
                                activeOpacity={0.7}
                              >
                                <Ionicons
                                  name={option.icon}
                                  size={14}
                                  color={contentColor}
                                />
                                <ThemedText
                                  style={[
                                    styles.desktopConditionLabel,
                                    { color: contentColor },
                                  ]}
                                  type="extraSmall"
                                >
                                  {option.label}
                                </ThemedText>
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                        <TouchableOpacity
                          style={styles.desktopRemarksToggle}
                          onPress={() =>
                            setExpandedItem(isExpanded ? null : item.name)
                          }
                          activeOpacity={0.7}
                        >
                          <ThemedText
                            style={[
                              styles.desktopRemarksToggleText,
                              { color: theme.icon },
                            ]}
                            type="extraSmall"
                          >
                            Add remarks
                          </ThemedText>
                          <Ionicons
                            name={
                              isExpanded ? "chevron-down" : "chevron-forward"
                            }
                            size={12}
                            color={theme.icon}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>

                    {isExpanded && (
                      <TextInput
                        style={[
                          styles.remarksInput,
                          {
                            backgroundColor: inputBackground,
                            borderColor: theme.border,
                            color: theme.text,
                          },
                        ]}
                        placeholder="Add any specific remarks or damage details..."
                        placeholderTextColor={theme.icon}
                        multiline
                        numberOfLines={3}
                        value={getItemRemarks(item.name)}
                        onChangeText={(text) =>
                          handleRemarksChange(item.name, text)
                        }
                      />
                    )}
                  </View>
                );
              })}
            </View>
          </ScrollView>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {showStickyTabs && (
        <View
          style={[
            styles.fixedTabBar,
            {
              backgroundColor: cardBackground,
              borderColor: theme.border,
            },
          ]}
        >
          {renderCategoryTabs(stickyTabScrollRef)}
        </View>
      )}

      <ScrollView
        ref={scrollRef}
        style={styles.scrollView}
        contentContainerStyle={[
          styles.contentContainer,
          keyboardHeight > 0 && generalRemarksFocused
            ? { paddingBottom: keyboardHeight + 24 }
            : null,
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        {inputCards}

        {fuelLevelCard}

        {checklistHeader}

        <View
          collapsable={false}
          onLayout={(event) => {
            setTabBarOffsetY(event.nativeEvent.layout.y);
          }}
          style={[
            styles.stickyTabBar,
            showStickyTabs && styles.tabBarPlaceholder,
            {
              backgroundColor: theme.background,
              borderColor: theme.border,
            },
          ]}
          pointerEvents={showStickyTabs ? "none" : "auto"}
        >
          {renderCategoryTabs(tabScrollRef)}
        </View>

        <View
          collapsable={false}
          onLayout={(event) => {
            checklistContainerYRef.current = event.nativeEvent.layout.y;
          }}
          style={[
            {
              backgroundColor: theme.background,
              borderColor: theme.border,
            },
          ]}
        >
          <View style={styles.checklistItems}>
            {(() => {
              let itemNumber = 0;

              return visibleCategories.map((category) => {
                const categoryChecklistItems = groupedItems[category.key] ?? [];

                if (categoryChecklistItems.length === 0) {
                  return null;
                }

                return (
                  <View
                    key={category.key}
                    collapsable={false}
                    onLayout={(event) => {
                      const sectionY = event.nativeEvent.layout.y;
                      categorySectionYRef.current[category.key] = sectionY;
                      categoryOffsetsRef.current[category.key] =
                        checklistContainerYRef.current + sectionY;
                    }}
                    style={styles.categorySection}
                  >
                    <View style={styles.categorySectionHeader}>
                      <Ionicons
                        name={category.icon as any}
                        size={18}
                        color={BrandColors.primary}
                      />
                      <ThemedText
                        style={styles.categorySectionTitle}
                        type="smallSemiBold"
                      >
                        {category.label}
                      </ThemedText>
                    </View>

                    {categoryChecklistItems.map((item) => {
                      itemNumber += 1;

                      return renderChecklistItem(item, itemNumber);
                    })}
                  </View>
                );
              });
            })()}
          </View>

          {remarksSections}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingBottom: 24,
    gap: 12,
  },
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 15,
  },
  requiredMark: {
    color: BrandColors.error,
    fontSize: 15,
  },
  odometerInputRow: {
    flexDirection: "row",
    alignItems: "stretch",
    borderWidth: 1,
    borderRadius: 12,
    overflow: "hidden",
    minHeight: 52,
  },
  odometerInput: {
    flex: 1,
    fontSize: 15,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  odometerSuffix: {
    minWidth: 52,
    alignItems: "center",
    justifyContent: "center",
    borderLeftWidth: 1,
    paddingHorizontal: 12,
  },
  odometerSuffixText: {
    fontSize: 14,
    fontWeight: "500",
  },
  fuelLevelRow: {
    flexDirection: "row",
    gap: 8,
  },
  fuelLevelOption: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderRadius: 12,
    gap: 8,
    minHeight: 88,
  },
  fuelLevelCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  fuelLevelCircleText: {
    color: BrandColors.white,
    fontSize: 12,
    fontWeight: "700",
  },
  fuelLevelLabel: {
    fontSize: 11,
    textAlign: "center",
    fontWeight: "500",
  },
  stickyTabBar: {
    borderBottomWidth: 1,
  },
  fixedTabBar: {
    position: "absolute",
    top: 0,
    left: 16,
    right: 16,
    zIndex: 10,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    elevation: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  tabBarPlaceholder: {
    opacity: 0,
  },
  checklistHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  checklistTitle: {
    fontSize: 15,
    flex: 1,
  },
  checklistProgress: {
    color: BrandColors.primary,
    fontSize: 12,
  },
  tabBar: {
    gap: 4,
  },
  tab: {
    alignItems: "center",
    paddingHorizontal: 10,
    paddingTop: 4,
    paddingBottom: 8,
    minWidth: 72,
    position: "relative",
  },
  tabLabel: {
    fontSize: 11,
    marginTop: 4,
    textAlign: "center",
  },
  tabIndicator: {
    position: "absolute",
    bottom: 0,
    left: 8,
    right: 8,
    height: 2,
    backgroundColor: BrandColors.primary,
    borderRadius: 1,
  },
  checklistItems: {
    marginTop: 12,
    gap: 12,
  },
  categorySection: {
    gap: 12,
  },
  categorySectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingTop: 4,
  },
  categorySectionTitle: {
    fontSize: 14,
    color: BrandColors.primary,
  },
  checklistItem: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
  },
  itemHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
    gap: 8,
  },
  itemTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flex: 1,
  },
  itemNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  itemNumberText: {
    fontSize: 11,
  },
  itemName: {
    fontSize: 14,
    flex: 1,
  },
  conditionBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  conditionBadgeText: {
    fontSize: 11,
  },
  conditionButtons: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 10,
  },
  conditionButton: {
    flex: 1,
    aspectRatio: 1,
    maxHeight: 44,
    borderRadius: 10,
    borderWidth: 1.5,
    alignItems: "center",
    justifyContent: "center",
  },
  remarksToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingTop: 2,
  },
  remarksToggleText: {
    color: BrandColors.primary,
    flex: 1,
    fontSize: 12,
  },
  remarksInput: {
    marginTop: 10,
    borderWidth: 1,
    borderRadius: 10,
    padding: 12,
    fontSize: 14,
    minHeight: 72,
    textAlignVertical: "top",
  },
  section: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
  },
  sectionTitle: {
    fontSize: 15,
    marginBottom: 8,
  },
  generalRemarksInput: {
    marginTop: 0,
    minHeight: 96,
  },
  warningLightsRow: {
    flexDirection: "row",
    gap: 12,
  },
  warningLightsOption: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderWidth: 1,
    borderRadius: 12,
  },
  obdHint: {
    marginBottom: 8,
  },
  desktopContainer: {
    flex: 1,
    flexDirection: "row",
    gap: 24,
    paddingHorizontal: 32,
  },
  desktopLeftColumn: {
    width: "36%",
    maxWidth: 420,
  },
  desktopLeftContent: {
    gap: 16,
    paddingBottom: 24,
  },
  desktopProgressTrack: {
    height: 4,
    borderRadius: 2,
    overflow: "hidden",
  },
  desktopProgressFill: {
    height: "100%",
    borderRadius: 2,
    backgroundColor: BrandColors.primary,
  },
  desktopRightColumn: {
    flex: 1,
  },
  desktopTabBar: {
    flexDirection: "row",
    borderBottomWidth: 1,
    marginBottom: 12,
  },
  desktopTab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 10,
    position: "relative",
  },
  desktopTabLabel: {
    fontSize: 12,
  },
  desktopItemsContent: {
    paddingBottom: 24,
  },
  desktopItemsCard: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  desktopItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  desktopItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  desktopItemActions: {
    flex: 2,
    alignItems: "center",
    gap: 6,
  },
  desktopConditionButtons: {
    flexDirection: "row",
    gap: 8,
    alignSelf: "stretch",
  },
  desktopConditionButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
  },
  desktopConditionLabel: {
    fontSize: 12,
  },
  desktopRemarksToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  desktopRemarksToggleText: {
    fontSize: 11,
  },
});
